import React, { useState, useEffect } from "react";
import { Menu, X, Languages } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../contexts/LanguageContext";

export default function Header({ onOpenLead }) {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "how", label: t.nav.work },
    { id: "pricing", label: t.nav.pricing },
    { id: "portfolio", label: t.nav.portfolio },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-16 md:h-24 flex items-center justify-between">
        <a href="#top" className="flex items-center group" data-testid="logo-link" aria-label="Launch Mitra">
          <img
            src={scrolled ? "/logo-light.png" : "/logo-dark.png"}
            alt="Launch Mitra"
            className="h-14 md:h-20 w-auto select-none"
            draggable="false"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className={`text-sm font-semibold transition relative group ${
                scrolled
                  ? "text-slate-700 hover:text-slate-950"
                  : "text-white/90 hover:text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.45)]"
              }`}
              data-testid={`nav-${l.id}`}
            >
              {l.label}
              <span className={`absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 ${scrolled ? "bg-gradient-to-r from-blue-600 to-violet-600" : "bg-gradient-to-r from-blue-400 to-violet-400"}`} />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={toggle}
            className={`flex h-9 px-3 items-center gap-1.5 rounded-full border text-sm font-semibold transition ${
              scrolled ? "border-slate-200 hover:border-slate-400 bg-white/70 text-slate-800" : "border-white/20 hover:border-white/40 bg-white/10 text-white"
            }`}
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Languages className="w-4 h-4" />
            <span>{lang === "en" ? "EN" : "हि"}</span>
          </button>

          <Button
            onClick={onOpenLead}
            className={`hidden md:inline-flex h-10 px-5 rounded-full font-semibold text-sm ${
              scrolled ? "bg-slate-950 hover:bg-slate-800 text-white" : "bg-white text-slate-950 hover:bg-white/90"
            }`}
            data-testid="header-cta-btn"
          >
            {t.nav.cta}
          </Button>

          <button
            className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center ${scrolled ? "bg-slate-100 text-slate-900" : "bg-white/10 text-white border border-white/20"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-5" data-testid="mobile-menu">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-slate-800 py-1.5"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => { toggle(); setOpen(false); }}
              className="mt-1 self-start h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-sm font-semibold"
              data-testid="mobile-lang-toggle"
            >
              <Languages className="w-4 h-4" />
              {lang === "en" ? "Switch to हिंदी" : "Switch to English"}
            </button>
            <Button
              onClick={() => { onOpenLead(); setOpen(false); }}
              className="mt-2 h-11 rounded-full bg-slate-950 text-white font-semibold"
              data-testid="mobile-header-cta"
            >
              {t.nav.cta}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
